'use client';

import { useState } from 'react';
import type { TFunction } from 'i18next';
import Link from 'next/link';
import type { Locale } from '@/lib/i18n-routes';
import { companyHref, courtCity, edgeLabel, fmtDateString } from './labels';
import type { GraphEdge, GraphNode } from './model';
import { formatRegisterNumber } from '@/new_components/company-detail/companyDetailUtils';
import styles from './network.module.css';

const LIST_LIMIT = 10;

/**
 * Details of the selected node beside the diagram: what it is, a few facts,
 * and every connection it has in the network (not only the visible ones).
 * Connections are buttons that select the other end.
 */
export function NetworkPanel({
  node,
  allEdges,
  nodeById,
  focalFacts,
  locale,
  t,
  onSelect,
}: {
  node: GraphNode;
  allEdges: GraphEdge[];
  nodeById: Map<string, GraphNode>;
  focalFacts: [string, string][];
  locale: Locale;
  t: TFunction;
  onSelect: (id: string) => void;
}) {
  const [showAll, setShowAll] = useState(false);

  if (node.type === 'group') {
    const members = node.members ?? [];
    return (
      <div aria-live="polite">
        <div className={styles.kind}>{t('companyNetwork.panel.group')}</div>
        <h3 className={styles.panelTitle}>
          {members[0]?.type === 'person'
            ? t('companyNetwork.group.persons', { count: members.length })
            : t('companyNetwork.group.companies', { count: members.length })}
        </h3>
        <ul className={styles.links}>
          {members.map((m) => (
            <li key={m.id}>
              <button type="button" onClick={() => onSelect(m.id)}>
                {m.name}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const raw = node.raw;
  const links = allEdges
    .filter((e) => e.up === node.id || e.down === node.id)
    .map((e) => ({ e, other: nodeById.get(e.up === node.id ? e.down : e.up) }))
    .filter((l): l is { e: GraphEdge; other: GraphNode } => !!l.other)
    .sort((a, b) => (b.e.share ?? -1) - (a.e.share ?? -1) || a.other.name.localeCompare(b.other.name, 'de'));
  const shown = showAll ? links : links.slice(0, LIST_LIMIT);

  const facts: [string, string][] = [];
  if (node.type === 'center') facts.push(...focalFacts);
  else if (node.type === 'person') {
    if (raw?.city) facts.push([t('companyNetwork.panel.city'), raw.city]);
    if (raw?.date_of_birth) facts.push([t('companyNetwork.panel.born'), fmtDateString(raw.date_of_birth, locale)]);
  } else {
    const seat = courtCity(raw?.register_court) ?? raw?.city;
    if (seat) facts.push([t('companyNetwork.panel.seat'), seat]);
    const reg = formatRegisterNumber(raw?.register_type, raw?.register_id);
    if (reg) facts.push([t('companyNetwork.panel.register'), reg]);
  }
  facts.push([t('companyNetwork.panel.connections'), String(links.length)]);
  const href = node.type === 'company' ? companyHref(raw, locale) : null;

  return (
    <div aria-live="polite">
      <div className={styles.kind}>
        {node.type === 'center'
          ? t('companyNetwork.panel.thisCompany')
          : node.type === 'person'
            ? t('companyNetwork.panel.person')
            : t('companyNetwork.panel.company')}
      </div>
      <h3 className={styles.panelTitle}>{node.name}</h3>
      <dl className={styles.facts}>
        {facts.map(([k, v]) => (
          <div key={k} className="contents">
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      {links.length > 0 && (
        <>
          <h4 className={styles.panelSub}>{t('companyNetwork.panel.connections')}</h4>
          <ul className={styles.links}>
            {shown.map(({ e, other }) => (
              <li key={e.id}>
                <button type="button" onClick={() => onSelect(other.id)}>
                  {other.name}
                </button>
                <span>{edgeLabel(e, locale, t)}</span>
              </li>
            ))}
          </ul>
          {links.length > LIST_LIMIT && (
            <button type="button" className={styles.more} onClick={() => setShowAll((v) => !v)}>
              {showAll
                ? t('companyNetwork.panel.showLess')
                : t('companyNetwork.panel.showAll', { count: links.length })}
            </button>
          )}
        </>
      )}
      {href && (
        <Link href={href} className={styles.go}>
          {t('companyNetwork.panel.toCompany')}
        </Link>
      )}
    </div>
  );
}
